import React from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  alpha,
  useTheme,
} from '@mui/material';
import { Analytics } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

function IndicatorsDialog({ open, onClose, indicators }) {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 3,
          },
        },
      }}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
            }}
          >
            <Analytics sx={{ color: 'white', fontSize: 24 }} />
          </Box>
          <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: -0.5 }}>
            {t('downtrend.indicators.title')}
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {indicators.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    '& th': {
                      fontWeight: 600,
                      fontSize: '0.875rem',
                      borderBottom: `2px solid ${alpha(theme.palette.divider, 0.2)}`,
                    },
                  }}
                >
                  <TableCell>{t('downtrend.indicators.code')}</TableCell>
                  <TableCell>{t('downtrend.indicators.name')}</TableCell>
                  <TableCell>{t('downtrend.indicators.group')}</TableCell>
                  <TableCell>{t('downtrend.indicators.sign')}</TableCell>
                  <TableCell>{t('downtrend.indicators.lag')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {indicators.map((indicator) => (
                  <TableRow
                    key={`${indicator.code}-${indicator.group}-${indicator.lag}`}
                    hover
                    sx={{
                      '&:hover': {
                        bgcolor: alpha(theme.palette.action.hover, 0.3),
                      },
                    }}
                  >
                    <TableCell>
                      <Chip
                        label={indicator.code}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {indicator.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {indicator.group}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {indicator.sign === 1 ? (
                        <Chip
                          label={t('downtrend.indicators.riskOff')}
                          color="error"
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      ) : (
                        <Chip
                          label={t('downtrend.indicators.riskOn')}
                          color="success"
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {indicator.lag} {t('downtrend.indicators.days')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body1">
              {t('downtrend.indicators.noIndicators')}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2.5 }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
          }}
        >
          {t('common.close', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

IndicatorsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  indicators: PropTypes.arrayOf(PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string,
    group: PropTypes.string,
    sign: PropTypes.number,
    lag: PropTypes.number,
  })).isRequired,
};

export default IndicatorsDialog;
